import React, { ReactNode } from "react";
import { GenericError } from "../generic-error";
import { captureError } from "@core-ui/utils/Error";

interface Props {
    errorContent?: ReactNode;
    children: ReactNode;
    errorFallback?: (error?: Error, errorInfo?: React.ErrorInfo) => void;
}

interface State {
    hasError: boolean;
}

export default class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidMount() {
        window.addEventListener("unhandledrejection", captureError);
        window.addEventListener("error", captureError);
    }

    componentWillUnmount() {
        window.removeEventListener("unhandledrejection", captureError);
        window.removeEventListener("error", captureError);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        __DEV__ && console.error(error);

        this.props.errorFallback && this.props.errorFallback?.(error, errorInfo);
        captureError(error);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    UNSAFE_componentWillReceiveProps(nextProps: Props): void {
        if (nextProps.children !== this.props.children) {
            this.setState({ hasError: false });
        }
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return this.props.errorContent !== undefined ? this.props.errorContent : <GenericError />;
        }

        return this.props.children;
    }
}
